import React, { useEffect, useState } from "react";
import { useAuthStore } from "../store/useAuthStore";
import { tError } from "../lib/toast";

import Icon from "../components/ui/Icon";
import Avatar from "../components/ui/Avatar";
import { t } from "../lib/theme";

const ROTATING = [
  "Send the first hello to a stranger ✨",
  "Make someone's day with a simple hi 💌",
  "Anonymous, kind, kind of magic 🌸",
  "One note. One reply. One connection. 💫",
];

const FEATURES = [
  { i: "send", tone: "primary", title: "Say hi", sub: "Drop a 280-char note" },
  { i: "shield", tone: "success", title: "Anonymous", sub: "Reveal only on match" },
  { i: "spark", tone: "accent", title: "Serendipity", sub: "Strangers, surprised" },
];

const TONES = {
  primary: { bg: t.primarySoft, fg: t.primary },
  success: { bg: t.successSoft, fg: t.success },
  accent: { bg: t.accentSoft, fg: t.accent },
};

const TelegramLoginPage = ({ telegramUser }) => {
  const { telegramLogin, isLoggingIn } = useAuthStore();
  const [tagline, setTagline] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setTagline((i) => (i + 1) % ROTATING.length),
      2800
    );
    return () => clearInterval(id);
  }, []);

  const handleLogin = async () => {
    if (!telegramUser) {
      tError("Cannot get Telegram user data");
      return;
    }
    if (!telegramUser.initData) {
      // We have an unsafe user blob but no signed envelope. Could happen in
      // PREVIEW mode or if the WebApp SDK didn't initialize. Backend would
      // reject, fail loudly here instead of silently 401-ing.
      tError("Missing Telegram signature — please reopen via the bot");
      return;
    }
    await telegramLogin({ initData: telegramUser.initData });
  };

  const name =
    telegramUser?.first_name || telegramUser?.username || "friend";

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: `radial-gradient(ellipse 90% 60% at 50% -10%, ${t.primarySoft} 0%, transparent 60%), ${t.bgGrad}`,
        padding: "32px 24px calc(env(safe-area-inset-bottom, 16px) + 28px)",
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Floating decorations */}
      <Floater style={{ top: "10%", left: "8%", animationDelay: "0s" }}>
        <span style={{ fontSize: 24 }}>💌</span>
      </Floater>
      <Floater style={{ top: "18%", right: "10%", animationDelay: "1.4s" }}>
        <span style={{ fontSize: 20 }}>✨</span>
      </Floater>
      <Floater style={{ top: "44%", left: "6%", animationDelay: "0.7s" }}>
        <span style={{ fontSize: 18 }}>🌸</span>
      </Floater>
      <Floater style={{ top: "38%", right: "6%", animationDelay: "2.1s" }}>
        <span style={{ fontSize: 22 }}>💫</span>
      </Floater>

      {/* Logo strip */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          fontSize: 12,
          color: t.inkMute,
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: 999,
            background: t.primary,
            boxShadow: `0 0 0 4px ${t.primarySoft}`,
          }}
        />
        SayHi · Telegram Mini App
      </div>

      {/* Hero */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 20,
          position: "relative",
        }}
      >
        <div
          style={{
            position: "relative",
            width: 140,
            height: 140,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Outer halo rings */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 999,
              border: `2px dashed ${t.primary}`,
              opacity: 0.35,
              animation: "spinSlow 18s linear infinite",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 18,
              borderRadius: 999,
              background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.5), transparent 60%), ${t.primaryGrad}`,
              boxShadow: t.shadow,
              animation: "pulse 2.6s ease-in-out infinite",
            }}
          />
          <div
            style={{
              position: "relative",
              color: "#FFF",
              transform: "translate(2px, -2px)",
              filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.18))",
            }}
          >
            <Icon name="send" size={56} sw={2} />
          </div>
        </div>

        <div>
          <div
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 38,
              fontWeight: 800,
              color: t.ink,
              letterSpacing: "-0.03em",
              lineHeight: 1.05,
            }}
          >
            Welcome to{" "}
            <span
              style={{
                background: t.primaryGrad,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              SayHi
            </span>
          </div>
          <div
            style={{
              height: 22,
              marginTop: 12,
              position: "relative",
              maxWidth: 300,
              margin: "12px auto 0",
            }}
          >
            {ROTATING.map((line, i) => (
              <div
                key={i}
                style={{
                  position: "absolute",
                  inset: 0,
                  fontSize: 14,
                  color: t.inkSoft,
                  fontWeight: 500,
                  lineHeight: 1.5,
                  opacity: i === tagline ? 1 : 0,
                  transform:
                    i === tagline ? "translateY(0)" : "translateY(6px)",
                  transition: "opacity 0.5s, transform 0.5s",
                }}
              >
                {line}
              </div>
            ))}
          </div>
        </div>

        {/* Feature triplet */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 8,
            width: "100%",
            maxWidth: 360,
            marginTop: 8,
          }}
        >
          {FEATURES.map((f) => {
            const c = TONES[f.tone];
            return (
              <div
                key={f.title}
                style={{
                  background: t.surface,
                  borderRadius: 18,
                  padding: "12px 10px",
                  textAlign: "center",
                  boxShadow: t.shadowSoft,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 11,
                    background: c.bg,
                    color: c.fg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon name={f.i} size={15} sw={2.2} />
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: t.ink,
                    letterSpacing: "-0.005em",
                  }}
                >
                  {f.title}
                </div>
                <div
                  style={{
                    fontSize: 10.5,
                    color: t.inkMute,
                    fontWeight: 500,
                    lineHeight: 1.4,
                  }}
                >
                  {f.sub}
                </div>
              </div>
            );
          })}
        </div>

        {/* User identity chip */}
        {telegramUser && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 18px 10px 10px",
              borderRadius: 999,
              background: t.surface,
              boxShadow: t.shadowSoft,
              marginTop: 4,
            }}
          >
            <Avatar seed={name} src={telegramUser?.photo_url} size={36} />
            <div style={{ textAlign: "left" }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: t.ink,
                }}
              >
                Hi, {name} 👋
              </div>
              {telegramUser?.username && (
                <div
                  style={{
                    fontSize: 11,
                    color: t.inkMute,
                    fontWeight: 600,
                  }}
                >
                  @{telegramUser.username}
                </div>
              )}
            </div>
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: 999,
                background: t.successSoft,
                color: t.success,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: 4,
              }}
            >
              <Icon name="checkCircle" size={14} sw={2.6} />
            </div>
          </div>
        )}
      </div>

      {/* CTA */}
      <button
        onClick={handleLogin}
        disabled={isLoggingIn}
        style={{
          all: "unset",
          cursor: isLoggingIn ? "not-allowed" : "pointer",
          textAlign: "center",
          padding: "18px",
          borderRadius: 20,
          background: t.primaryGrad,
          color: "#FFF",
          fontSize: 16,
          fontWeight: 800,
          letterSpacing: "0.005em",
          boxShadow: t.shadow,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          opacity: isLoggingIn ? 0.7 : 1,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {isLoggingIn ? (
          <div
            style={{
              width: 18,
              height: 18,
              border: "2px solid #FFF",
              borderTopColor: "transparent",
              borderRadius: 999,
              animation: "spin 0.8s linear infinite",
            }}
          />
        ) : (
          <>
            <Icon name="send" size={16} sw={2.2} /> Continue with Telegram
          </>
        )}
        {/* Shine sweep */}
        {!isLoggingIn && (
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              width: 60,
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
              transform: "skewX(-20deg)",
              animation: "shine 2.6s ease-in-out infinite",
            }}
          />
        )}
      </button>
      <div
        style={{
          fontSize: 11,
          color: t.inkMute,
          textAlign: "center",
          marginTop: 12,
          fontWeight: 500,
          letterSpacing: "0.01em",
        }}
      >
        Anonymous · Be kind · Have fun ✨
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes spinSlow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.04); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        @keyframes shine {
          0% { left: -80px; }
          60%, 100% { left: 110%; }
        }
      `}</style>
    </div>
  );
};

const Floater = ({ style, children }) => (
  <div
    style={{
      position: "absolute",
      animation: "float 5s ease-in-out infinite",
      filter: "drop-shadow(0 3px 8px rgba(31,20,24,0.08))",
      pointerEvents: "none",
      userSelect: "none",
      ...style,
    }}
  >
    {children}
  </div>
);

export default TelegramLoginPage;
